import React, { Component } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    MenuItem,
    Tooltip,
    Typography,
    Grid
} from '@material-ui/core'
import { Add as AddIcon, Delete as DeleteIcon, TurnedIn as TurnedIcon, Close as CloseIcon } from '@material-ui/icons'
import * as CONSTANTS from '../../../utils/constants'
import * as FINANCING from '../../../redux/actions/financingCosts'
import * as NOTIFICATION from '../../../utils/notification'
import NumberFormatCustom from '../../common/NumberFormatCustom'
import SimpleMenu from '../../common/SimpleMenu'
import ConfirmationDialog from '../../common/ConfirmationDialog'

const todayValue = moment().format(CONSTANTS.INPUT_TYPE_DATE_FORMAT)

const REPRESENTING_OPTIONS = [
    { value: 'APORT', label: 'Aport' },
    { value: 'TAXE_ADMINISTRATIVE', label: 'Taxe administrative' },
    { value: 'TAXE_LOGISTICE', label: 'Taxe logistice' },
    { value: 'TAXE_CESIUNE', label: 'Taxe cesiune' },
    { value: 'ALTE_TAXE', label: 'Alte Taxe' },
    { value: 'DOBANDA', label: 'Dobanda' },
    { value: 'COMISION', label: 'Comision' },
    { value: 'ALTELE', label: 'Altele' }
]

const emptyRow = () => ({
    amount: '',
    representing: '',
    comment: '',
    date: todayValue
})

class FinancingCostModalV2 extends Component {

    state = {
        contractNumber: '',
        contractDate: todayValue,
        rows: [emptyRow()],
        financedValue: 0,
        totalContribution: 0,
        remainedValue: 0,
        financingCost: 0,
        isClosed: false,
        closeFinancingState: null,
        openFinancingConfirmation: false
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.resetForm(() => {
                if (this.props.type === CONSTANTS.EDIT) {
                    this.loadExistingData()
                }
            })
        }
    }

    componentDidMount() {
        if (this.props.type === CONSTANTS.EDIT && this.props.open) {
            this.resetForm(() => {
                this.loadExistingData()
            })
        }
    }

    resetForm = (callback) => {
        this.setState({
            contractNumber: '',
            contractDate: todayValue,
            rows: [emptyRow()],
            financedValue: 0,
            totalContribution: 0,
            remainedValue: 0,
            financingCost: 0,
            isClosed: false,
            closeFinancingState: null,
            openFinancingConfirmation: false
        }, callback)
    }

    loadExistingData = () => {
        if (!this.props.stock || !this.props.stock.financingCostV2Id) return

        this.props.getById(this.props.stock.financingCostV2Id, true).then(result => {
            const rows = result.contributions && result.contributions.length
                ? result.contributions.map(c => ({
                    amount: c.amount || '',
                    representing: c.representing || '',
                    comment: c.comment || '',
                    date: c.date ? moment(c.date).format(CONSTANTS.INPUT_TYPE_DATE_FORMAT) : todayValue
                }))
                : [emptyRow()]

            this.setState({
                contractNumber: result.contractNumber || '',
                contractDate: result.contractDate
                    ? moment(result.contractDate).format(CONSTANTS.INPUT_TYPE_DATE_FORMAT)
                    : todayValue,
                rows,
                isClosed: result.isClosed || false
            }, this.recalculate)
        })
    }

    recalculate = () => {
        const { stock } = this.props
        if (!stock) return

        let purchaseTotal = 0
        if (stock.type === 'sh' && stock.ShPurchasePrice) {
            purchaseTotal = Number(stock.ShPurchasePrice * stock.purchaseExchangeRate) * 1.21
        } else {
            purchaseTotal = Number((stock.purchasePrice || 0) * (stock.purchaseExchangeRate || 1)) * 1.21
        }

        // First APORT row amount
        const firstAport = this.state.rows.find(row => row.representing === 'APORT')
        const firstAportAmount = firstAport ? (Number(firstAport.amount) || 0) : 0

        // Financed value = purchaseTotal - first APORT entry
        const financedValue = purchaseTotal - firstAportAmount

        // Aport Total = sum of all APORT rows
        const totalContribution = this.state.rows
            .filter(row => row.representing === 'APORT')
            .reduce((sum, row) => sum + (Number(row.amount) || 0), 0)

        // Remained value = purchaseTotal (valoarea de intrare) - aport total
        const remainedValue = purchaseTotal - totalContribution

        // Cost finantare = sum of TAXE_ADMINISTRATIVE + TAXE_LOGISTICE + TAXE_CESIUNE + ALTE_TAXE + DOBANDA + COMISION + ALTELE
        const costTypes = ['TAXE_ADMINISTRATIVE', 'TAXE_LOGISTICE', 'TAXE_CESIUNE', 'ALTE_TAXE', 'DOBANDA', 'COMISION', 'ALTELE']
        const financingCost = this.state.rows
            .filter(row => costTypes.includes(row.representing))
            .reduce((sum, row) => sum + (Number(row.amount) || 0), 0)

        this.setState({
            financedValue,
            totalContribution,
            remainedValue,
            financingCost
        })
    }

    handleHeaderChange = (field, value) => {
        this.setState({ [field]: value }, this.recalculate)
    }

    handleRowChange = (index, field, value) => {
        const rows = [...this.state.rows]
        rows[index] = { ...rows[index], [field]: value }
        this.setState({ rows }, this.recalculate)
    }

    addRow = () => {
        this.setState(prev => ({
            rows: [...prev.rows, emptyRow()]
        }), this.recalculate)
    }

    removeRow = (index) => {
        if (this.state.rows.length <= 1) return
        this.setState(prev => ({
            rows: prev.rows.filter((_, i) => i !== index)
        }), this.recalculate)
    }

    onSaveHandler = () => {
        const { contractNumber, contractDate, rows } = this.state

        if (this.props.stock && this.props.stock.contractDate) {
            if (moment(contractDate).isBefore(moment(this.props.stock.contractDate))) {
                NOTIFICATION.error(this.props.language.cost.financing.edit.contractDateError || 'Contract date must be after stock contract date')
                return
            }
        }

        const payload = {
            contractNumber,
            contractDate,
            contributions: rows.map(row => ({
                amount: row.amount ? Number(row.amount) : 0,
                representing: row.representing,
                comment: row.comment,
                date: row.date
            })),
            financedValue: this.state.financedValue,
            totalContribution: this.state.totalContribution,
            remainedValue: this.state.remainedValue,
            financingCost: this.state.financingCost
        }

        if (this.props.type === CONSTANTS.EDIT && this.props.stock.financingCostV2Id) {
            this.props.edit(this.props.stock.financingCostV2Id, payload).then(() => {
                NOTIFICATION.success(this.props.language.cost.financing.edit.success)
                this.props.onSave()
            }).catch(() => {
                NOTIFICATION.error(this.props.language.cost.financing.edit.failure)
            })
        } else {
            payload.stockId = this.props.stock.id
            this.props.createFinancing(payload).then(() => {
                NOTIFICATION.success(this.props.language.cost.financing.create.success)
                this.props.onSave()
            }).catch(() => {
                NOTIFICATION.error(this.props.language.cost.financing.create.failure)
            })
        }
    }

    openFinancingConfirmation = value => {
        this.setState({
            openFinancingConfirmation: true,
            closeFinancingState: value
        })
    }

    closeFinancingConfirmation = () => {
        this.setState({
            openFinancingConfirmation: false,
            closeFinancingState: null
        })
    }

    onCloseFinancing = () => {
        if (this.state.closeFinancingState)
            this.props.edit(this.props.stock.financingCostV2Id, {
                isClosed: true,
                closedType: this.state.closeFinancingState
            }).then(() => {
                NOTIFICATION.success(this.props.language.cost.financing.edit.success)
                this.setState({ openFinancingConfirmation: false, closeFinancingState: null })
                this.props.onSave()
            }).catch(() => {
                NOTIFICATION.error(this.props.language.cost.financing.edit.failure)
            })
    }

    formatNumber = (value) => {
        return Number(value || 0).toFixed(2)
    }

    render() {
        const { open, onCancel, language } = this.props
        const lang = language.cost.financing
        const fieldLang = language.itemDetails.fCost

        return (
            <>
                <ConfirmationDialog
                    titleText={lang.close.confirmation}
                    text={this.state.closeFinancingState === 'WITH_RESTITUTION'
                        ? lang.close.withRestitution
                        : lang.close.withoutRestitution}
                    open={this.state.openFinancingConfirmation}
                    acceptButtonText={lang.close.confirm}
                    cancelButtonText={lang.close.cancel}
                    onAccept={this.onCloseFinancing}
                    onClose={this.closeFinancingConfirmation}
                    onCancel={this.closeFinancingConfirmation}
                />
                <Dialog
                    open={open}
                    onClose={() => {}}
                    disableBackdropClick
                    disableEscapeKeyDown
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{display: 'inline-block', width: '100%'}}>
                            {this.props.type === CONSTANTS.CREATE
                                ? (lang.formModal.add || 'Add Financing Cost')
                                : (lang.formModal.title || 'Edit Financing Cost')}
                        </span>
                        <CloseIcon
                            onClick={onCancel}
                            style={{ cursor: 'pointer', fontSize: 18, marginRight: 4, position: 'absolute', right: 50, top: 30 }}
                        />
                    </DialogTitle>
 
                    <DialogContent style={{ padding: 24 }}>
                        {/* Contract Number + Contract Date */}
                        <Grid container spacing={2} style={{ marginBottom: 16 }}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label={fieldLang.contractNumber || 'Nr Contract'}
                                    value={this.state.contractNumber}
                                    onChange={e => this.handleHeaderChange('contractNumber', e.target.value)}
                                    disabled={this.state.isClosed}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label={fieldLang.contractDate || 'Data Contract'}
                                    value={this.state.contractDate}
                                    onChange={e => this.handleHeaderChange('contractDate', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    disabled={this.state.isClosed}
                                    variant="outlined"
                                    size="small"
                                />
                            </Grid>
                        </Grid>

                        {/* Dynamic Rows */}
                        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                            {lang.contributions || 'Contributions'}
                        </Typography>
                        {this.state.rows.map((row, index) => (
                            <Grid container spacing={2} key={index} style={{ marginBottom: 8 }} alignItems="center">
                                <Grid item xs={3}>
                                    <TextField
                                        fullWidth
                                        label={lang.amount || 'Suma'}
                                        value={row.amount}
                                        onChange={e => this.handleRowChange(index, 'amount', e.target.value)}
                                        disabled={this.state.isClosed}
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            inputComponent: NumberFormatCustom
                                        }}
                                    />
                                </Grid>
                                <Grid style={{ height: 56, marginLeft: 10 }} item xs={3}>
                                    <TextField
                                        style={{ height: 56 }}
                                        inputProps={{ style: { height: 56, padding: '0 14px' } }}
                                        fullWidth
                                        select
                                        label={lang.representing || 'Reprezentand'}
                                        value={row.representing}
                                        onChange={e => this.handleRowChange(index, 'representing', e.target.value)}
                                        disabled={this.state.isClosed}
                                        variant="outlined"
                                        size="small"
                                    >
                                        {REPRESENTING_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item style={{ marginLeft: 10 }} xs={3}>
                                    <TextField
                                        fullWidth
                                        label={lang.comment || 'Comment'}
                                        value={row.comment}
                                        onChange={e => this.handleRowChange(index, 'comment', e.target.value)}
                                        disabled={this.state.isClosed}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid style={{ marginLeft: 10 }} item xs={2}>
                                    <TextField
                                        fullWidth
                                        type="date"
                                        label={lang.date || 'Data'}
                                        value={row.date}
                                        onChange={e => this.handleRowChange(index, 'date', e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        disabled={this.state.isClosed}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                                <Grid style={{ float: 'left', flexBasis: '0' }} item xs={1}>
                                    <IconButton
                                        size="small"
                                        onClick={() => this.removeRow(index)}
                                        disabled={this.state.rows.length <= 1 || this.state.isClosed}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))}

                        <Button
                            size="small"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={this.addRow}
                            disabled={this.state.isClosed}
                            style={{ marginBottom: 16 }}
                        >
                            {lang.addRow || 'Add Row'}
                        </Button>

                        {/* Auto-calculated fields */}
                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={fieldLang.financedValue || 'Val Finantata'}
                                    value={this.formatNumber(this.state.financedValue)}
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title="Formula">
                                                <TurnedIcon fontSize="small" />
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={lang.totalContribution || 'Aport Total'}
                                    value={this.formatNumber(this.state.totalContribution)}
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title="Formula">
                                                <TurnedIcon fontSize="small" />
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={fieldLang.remainedValue || 'Suma Ramasa'}
                                    value={this.formatNumber(this.state.remainedValue)}
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title="Formula">
                                                <TurnedIcon fontSize="small" />
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    label={lang.financingCost || 'Cost Finantare'}
                                    value={this.formatNumber(this.state.financingCost)}
                                    disabled
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <Tooltip title="Formula">
                                                <TurnedIcon fontSize="small" />
                                            </Tooltip>
                                        )
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                       {this.props.type === CONSTANTS.EDIT && (
                            <SimpleMenu
                                style={{ flex: '0.1'}}
                                disabled={this.state.isClosed}
                                text={lang.close.button}
                                options={[
                                    { label: lang.close.menu.withRestitution, value: 'WITH_RESTITUTION' },
                                    { label: lang.close.menu.withoutRestitution, value: 'WITHOUT_RESTITUTION' }
                                ]}
                                onClick={this.openFinancingConfirmation}
                            />
                        )}
                        <Button style={{flex: 0.1, marginLeft: 'auto'}} onClick={onCancel} color="secondary">
                            {lang.confirmationDialog.cancelButtonText || 'Cancel'}
                        </Button>
                        <Button
                            onClick={this.onSaveHandler}
                            color="primary"
                            variant="contained"
                            disabled={this.state.isClosed}
                        >
                            {this.props.type === CONSTANTS.CREATE
                                ? (lang.confirmationDialog.submitButtonText || 'Save')
                                : (lang.formModal.edit || 'Edit')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        )
    }
}

const mapStateToProps = state => ({
    language: state.languageReducer.i18n,
    login: state.loginReducer
})

const mapDispatchToProps = dispatch => ({
    createFinancing: financingCost => dispatch(FINANCING.createv2(financingCost)),
    getById: (financingCostId, blocked) => dispatch(FINANCING.getByIdv2(financingCostId, blocked)),
    edit: (financingCostId, financingCost) => dispatch(FINANCING.editv2(financingCostId, financingCost))
})

export default connect(mapStateToProps, mapDispatchToProps)(FinancingCostModalV2)
